import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  Subscription,
} from 'rxjs';
import { UserService } from 'src/app/services/user.service';
import { User } from 'src/app/shared/models/user.model';
import { NgIf, NgFor, TitleCasePipe } from '@angular/common';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { DeleteUserConfirmationComponent } from '../../../shared/components/delete-user-confirmation/delete-user-confirmation.component';
import { ModalComponent } from '../../../shared/components/modal/modal.component';

type MoreAvailableUsers = {
  visible: boolean;
  current_page: number;
};

@Component({
    selector: 'app-users',
    templateUrl: './users.component.html',
    styleUrls: ['./users.component.scss'],
    imports: [
      ModalComponent,
      DeleteUserConfirmationComponent,
      FormsModule,
      ReactiveFormsModule,
      NgIf,
      NgFor,
      MatMenuTrigger,
      MatIcon,
      MatMenu,
      MatMenuItem,
        TitleCasePipe,
    ],
})
export class UsersComponent implements OnInit, OnDestroy {
  private readonly subscriptions = new Subscription();

  public readonly form = new UntypedFormGroup({
    search: new UntypedFormControl(''),
  });

  public readonly searchState = signal<null | 'new' | 'more'>(null);

  public readonly lookupError = signal(false);

  public readonly results = signal<User[]>([]);

  public readonly showResults = signal(false);

  public readonly selectedUser = signal<User | null>(null);

  public readonly moreAvailableUsers = signal<MoreAvailableUsers>({
    visible: false,
    current_page: 1,
  });

  public readonly showDeleteAccountModal = signal(false);

  public readonly deleteUserTitle = 'Delete User';

  public readonly deleteUserMessage =
    'Are you sure you want to delete this user? This action cannot be undone.';

  public constructor(private readonly userService: UserService) {}

  ngOnInit(): void {
    this.initForm();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public get searchControl(): UntypedFormControl {
    return this.form.controls['search'] as UntypedFormControl;
  }

  private initForm(): void {
    this.subscriptions.add(
      this.searchControl.valueChanges
        .pipe(debounceTime(400), distinctUntilChanged())
        .subscribe((value: string) => {
          if (value.length < 3) return;
          this.search();
        })
    );
  }

  public search(loadMore: boolean = false): void {
    this.lookupError.set(false);
    this.searchState.set(loadMore ? 'more' : 'new');
    if (!loadMore) {
      this.moreAvailableUsers.set({
        ...this.moreAvailableUsers(),
        current_page: 1,
      });
    }

    this.userService
      .search(this.searchControl.value as string, this.moreAvailableUsers().current_page)
      .pipe(
        finalize(() => {
          this.searchState.set(null);
        })
      )
      .subscribe({
        next: (res) => {
          if (res.data?.next_page_url) {
            this.moreAvailableUsers.set({
              visible: true,
              current_page: res.data.current_page + 1,
            });
          } else {
            this.moreAvailableUsers.set({
              ...this.moreAvailableUsers(),
              visible: false,
            });
          }
          const users = (res.data?.data.map((user: User) =>
            User.fromJson(user)
          ));
          this.results.set(loadMore ? [...this.results(), ...users] : users);
          this.showResults.set(true);
        },
        error: () => {
          this.lookupError.set(true);
        },
      });
  }

  public onSearchBlur(): void {
    this.showResults.set(false);
  }

  public addRole(user: User, role: string): void {
    this.userService.addRole(user, role).subscribe(() => {
      this.search();
    });
  }

  public removeRole(user: User, role: string): void {
    this.userService.removeRole(user, role).subscribe(() => {
      this.search();
    });
  }

  toggleDeleteModal(user: User | null = null) {
    this.selectedUser.set(user);
    this.showDeleteAccountModal.set(!this.showDeleteAccountModal());
  }

  public deleteUser(user: User): void {
    this.userService.hardDeleteUser(user).subscribe(() => {
      this.showDeleteAccountModal.set(false);
      this.selectedUser.set(null);
      this.search();
    });
  }
}
